import { listVisibleWorkoutPlans } from "@/lib/fitness/planLibrary";
import { supabase } from "@/integrations/supabase/client";
import type { WorkoutSessionRow } from "@/lib/fitness/sessionKind";

export const localFitnessDay = (date: Date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;

export async function readFitnessHistory(userId: string, assertCurrent: () => void): Promise<WorkoutSessionRow[]> {
  const rows: WorkoutSessionRow[] = [];
  const now = new Date();
  for (let offset = 0; ; offset += 500) {
    assertCurrent();
    const { data, error } = await (supabase as any).from("workout_sessions")
      .select("id,user_id,name,exercises,source,external_id,plan_id,started_at,completed_at,duration_minutes,calories_burned,notes,exercises_completed,sets_completed,total_sets")
      .eq("user_id", userId).not("completed_at", "is", null).lte("completed_at", now.toISOString())
      .order("completed_at", { ascending: false }).order("id", { ascending: false }).range(offset, offset + 499);
    assertCurrent(); if (error || !Array.isArray(data)) throw error ?? new Error("Missing fitness history");
    rows.push(...data); if (data.length < 500) return rows;
  }
}

/** Plans are sequences, not a made-up weekday calendar. */
export function nextPlanDay(plan: any, lastSession?: { name?: string | null; exercises?: unknown } | null): any | null {
  const days = Array.isArray(plan?.days) ? plan.days : [];
  if (!days.length) return null;
  let previous = lastSession ? days.findIndex((day: any) => (day.name || plan.name) === lastSession.name) : -1;
  // Renaming the history entry must not restart an otherwise identifiable plan.
  if (previous < 0 && Array.isArray(lastSession?.exercises)) {
    const names = lastSession.exercises.map((exercise: any) => exercise?.name);
    const matches = days.map((day: any, index: number) => ({ day, index })).filter(({ day }: any) =>
      names.length > 0 && Array.isArray(day.exercises) && day.exercises.length === names.length && day.exercises.every((exercise: any, index: number) => exercise?.name === names[index]));
    if (matches.length === 1) previous = matches[0].index;
  }
  return days[(previous + 1) % days.length];
}

export async function readFitnessHub(userId: string, assertCurrent: () => void) {
  const now = new Date(); const weekStart = new Date(now); weekStart.setDate(weekStart.getDate() - 6); weekStart.setHours(0, 0, 0, 0);
  const [{ data: profile, error: profileError }, { data: plan, error: planError }, week, sleep] = await Promise.all([
    (supabase as any).from("fitness_profiles").select("id,days_per_week").eq("user_id", userId).maybeSingle(),
    (supabase as any).from("workout_plans").select("id,name,days").eq("user_id", userId).eq("is_active", true).order("created_at", { ascending: false }).limit(1).maybeSingle(),
    (supabase as any).from("workout_sessions").select("completed_at").eq("user_id", userId).gte("completed_at", weekStart.toISOString()).lte("completed_at", now.toISOString()),
    (supabase as any).from("sleep_logs").select("score").eq("user_id", userId).eq("sleep_date", localFitnessDay(now)).maybeSingle(),
  ]);
  assertCurrent(); if (profileError || planError || week.error || !Array.isArray(week.data)) throw profileError ?? planError ?? week.error ?? new Error("Missing training days");
  let last = null;
  if (plan) {
    const result = await (supabase as any).from("workout_sessions").select("name,exercises").eq("user_id", userId).eq("plan_id", plan.id).not("completed_at", "is", null).lte("completed_at", new Date().toISOString()).order("completed_at", { ascending: false }).order("id", { ascending: false }).limit(1).maybeSingle();
    assertCurrent(); if (result.error) throw result.error; last = result.data;
  }
  return { profile, nextWorkout: nextPlanDay(plan, last), trainingDays: new Set(week.data.map((row: any) => localFitnessDay(new Date(row.completed_at)))).size, sleepScore: sleep.error ? null : sleep.data?.score ?? null };

}

export async function readFitnessPlans(userId: string, assertCurrent: () => void) {
  const { data, error } = await listVisibleWorkoutPlans();
  assertCurrent(); if (error || !Array.isArray(data)) throw error ?? new Error("Missing fitness plans");
  if (data.some((plan: { user_id?: string }) => plan.user_id !== userId)) throw new Error("Fitness account changed");
  return data as any[];
}
